/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */





//  CARD PRODUCT SWATXHES AJAX JS START
// Use event delegation on document

document.addEventListener("DOMContentLoaded", () => {

// document.addEventListener("click", async (e) => {
//   const targetDiv = e.target.closest(".each_card-product-color-options_li div");
//   if (!targetDiv) return;

//   // Ensure the clicked div is inside a color-options container
//   if (!targetDiv.closest(".card-product-color-options")) return;

//   const cardproducturl = targetDiv.getAttribute("data-cardproducturl");
//   if (!cardproducturl) return;

//   console.log("Fetching main product:", cardproducturl);

//   try {
//     // 1️⃣ Fetch main product HTML
//     const response = await fetch(cardproducturl);
//     if (!response.ok) throw new Error("Failed to fetch product HTML");
//     const html = await response.text();

//     const parser = new DOMParser();
//     const doc = parser.parseFromString(html, "text/html");

//     // 2️⃣ Select the fetched and current product blocks
//     const fetchedMain = doc.querySelector("product-block");

//     // ✅ This finds the product-block that contains the clicked color option
//     const currentMain = targetDiv.closest("product-block");

//     if (fetchedMain && currentMain) {
//       currentMain.innerHTML = fetchedMain.innerHTML;
//     }

//   } catch (error) {
//     console.error("Error fetching product or related products:", error);
//   }
// });


document.addEventListener("click", async (e) => {
  const targetDiv = e.target.closest(".each_card-product-color-options_li div");
  if (!targetDiv) return;

  const cardproducturl = targetDiv.getAttribute("data-cardproducturl");
  if (!cardproducturl) return;

  const response = await fetch(cardproducturl);
  const html = await response.text();

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const fetchedMain = doc.querySelector("product-block");
  const currentMain = targetDiv.closest("product-block");

  if (fetchedMain && currentMain) {
    currentMain.innerHTML = fetchedMain.innerHTML;

    // ✅ Custom re-init
    if (typeof currentMain.init === "function") currentMain.init();
  }
});





//////
document.addEventListener("click", async (e) => {
  const targetDiv = e.target.closest(".each_upsell-color-options_li div");
  if (!targetDiv) return;

  const upsellurl = targetDiv.getAttribute("data-upsellurl");
  if (!upsellurl) return;

  const response = await fetch(upsellurl);
  const html = await response.text();

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const fetchedMain = doc.querySelector(".up_sell_prod_each");
  const currentMain = targetDiv.closest(".up_sell_prod_each");
console.log("clickedd at");
  if (fetchedMain && currentMain) {
    currentMain.innerHTML = fetchedMain.innerHTML;

    // ✅ Custom re-init
    if (typeof currentMain.init === "function") currentMain.init();
  }
});



// mobile dropdown upsell
document.addEventListener("click", async (e) => {
  const targetBtn = e.target.closest(".up_sell_prod_dropdown_btn_mob");
  if (!targetBtn) return;

  // Find the parent container
  const parentDiv = targetBtn.closest(".up_sell_prod_each");
  if (!parentDiv) return;

  // Add class to that parent
  parentDiv.classList.add("popup-open");
  document.body.classList.add("body_popup-open");
});

// close popup upsell
document.addEventListener("click", async (e) => {
  const targetcrossBtn = e.target.closest(".cross_quick_buy");
  if (!targetcrossBtn) return;

  // Find the parent container
  const parentcrossDiv = targetcrossBtn.closest(".up_sell_prod_each");
  if (!parentcrossDiv) return;

  // Add class to that parent
  parentcrossDiv.classList.remove("popup-open");
  document.body.classList.remove("body_popup-open");
  const innerDiv = parentcrossDiv.querySelector(".up_sell_prod_each-inn");
if (innerDiv) {
  innerDiv.classList.remove("up_sell_prod_each-inn-ajax");
}
});




// add to cart upsell
// document.addEventListener("click", async (e) => {
//   const buyDiv = e.target.closest(".up_sell_prod_buy_button");
//   if (!buyDiv) return;

//   const variantId = buyDiv.dataset.selectedid;
//   if (!variantId) {
//     alert("No variant selected!");
//     return;
//   }

//   try {
//     const response = await fetch("/cart/add.js", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         "Accept": "application/json"
//       },
//       body: JSON.stringify({
//         id: variantId,
//         quantity: 1
//       })
//     });

//     const data = await response.json();
//     console.log("Added to cart:", data);

//     // Optional: show confirmation or update mini-cart
//     // alert(`Added ${data.title} to cart!`);
//   } catch (err) {
//     console.error("Error adding to cart:", err);
//     alert("Failed to add to cart.");
//   }
// });

document.addEventListener("click", async (e) => {
  const buyDiv = e.target.closest(".up_sell_prod_buy_button");
  if (!buyDiv) return;

  const variantId = buyDiv.dataset.selectedid;
  if (!variantId) {
    alert("No variant selected! OR Variant not available");
    return;
  }

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    const data = await response.json();
    console.log("Added to cart:", data);

    // Refresh the side cart
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
      bubbles: true
    }));

    // Open the side cart drawer
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
      detail: { opener: buyDiv } // optional, passes the clicked element
    }));

  } catch (err) {
    console.error("Error adding to cart:", err);
    alert("Failed to add to cart.");
  }
});




// on variant change in upsell get the varuiant ID
document.addEventListener("click", (e) => {
  let isProdAvailable = true;
  const clickedLi = e.target.closest(".option-selector__btns-upsell li");
  if (!clickedLi) return; // Only handle clicks on your li options

  const picker = clickedLi.closest(".cstm-variant-picker-upsell");
  const parentProd = clickedLi.closest(".up_sell_prod_each-inn");
  if (!picker || !parentProd) return;

  const prodId = parentProd.dataset.prodid;
  if (!prodId) return;

  

 const parentUl = clickedLi.closest(".option-selector__btns-upsell");
  if (!parentUl) return;

  // Remove 'selected' from all siblings inside this same <ul>
  parentUl.querySelectorAll("li.selected").forEach(li => {
    li.classList.remove("selected");
  });


  // 🧩 Remove previous selection and mark clicked li
  // picker.querySelectorAll("li").forEach(li => li.classList.remove("selected"));

  
  clickedLi.classList.add("selected");

  // 🧩 Collect selected options for this upsell product
  const selectedOptions = {};
  picker.querySelectorAll(".option-selector-upsell").forEach(selector => {
   
    const optionName = selector.dataset.option;
    const selectedLi = selector.querySelector("li.selected");
      setTimeout(() => {
      if (selectedLi.classList.contains("unavailable")) {
      isProdAvailable = false;
      }
      }, 100);
    
    if (selectedLi) selectedOptions[optionName] = selectedLi.getAttribute("value");
  });
console.log("isProdAvailable", isProdAvailable);
  console.log("selectedOptions", selectedOptions);

  // 🧩 Get and parse JSON for this product
  const scriptEl = parentProd.querySelector(`#upsellJson-${prodId}`);
  if (!scriptEl) return;

  let productJson;
  try {
    productJson = JSON.parse(scriptEl.textContent);
  } catch {
    console.error(`Invalid JSON for upsell product ${prodId}`);
    return;
  }

  console.log("productJson", productJson);

  // 🧩 Call the function HERE ✅
  // updateUnavailableOptions(picker, productJson, selectedOptions);
   updateUnavailableOptions(picker, productJson);

  // 🧩 Match the correct variant
  const matchedVariant = productJson.variants.find(variant =>
    Object.values(selectedOptions).every((val, idx) => val === variant[`option${idx + 1}`])
  );

  // 🧩 Update the buy button with matched variant ID
  const buyButtonDiv = parentProd.querySelector(".up_sell_prod_buy_button");
 
   if (buyButtonDiv) {
    setTimeout(() => {
    if (isProdAvailable) {
      buyButtonDiv.classList.remove("disable");
    } else {
      buyButtonDiv.classList.add("disable");
    }
    }, 200);
  }

  if (matchedVariant) {
    buyButtonDiv.dataset.selectedid = matchedVariant.id;
    buyButtonDiv.classList.add("variant-found");
    console.log(`✅ Variant found for ${prodId}:`, matchedVariant.id);
  } else {
    buyButtonDiv.dataset.selectedid = "";
    buyButtonDiv.classList.remove("variant-found");
    console.warn(`❌ No matching variant for ${prodId}`);
  }
});




// updateUnavailableOptionsJS
// function updateUnavailableOptions(picker, productJson, selectedOptions = {}) {
//   if (!picker || !productJson?.variants) return;

//   const allSelectors = picker.querySelectorAll(".option-selector-upsell");

//   allSelectors.forEach((selector) => {
//     const optionName = selector.dataset.option;
//     const optionsLis = selector.querySelectorAll("li");

//     optionsLis.forEach((li) => {
//       const thisValue = li.getAttribute("value");

//       // Clone the current selection and simulate choosing this option
//       const tempSelection = { ...selectedOptions, [optionName]: thisValue };

//       // Check if this combination exists in product variants
//       const comboExists = productJson.variants.some((variant) => {
//         return Object.entries(tempSelection).every(([name, val], idx) => {
//           return val === variant[`option${idx + 1}`];
//         });
//       });

//       // Toggle unavailable class based on existence
//       if (comboExists) {
//         li.classList.remove("unavailable");
//       } else {
//         li.classList.add("unavailable");
//       }
//     });
//   });
// }
// function updateUnavailableOptions(picker, productJson, selectedOptions = {}) {
//   if (!picker || !productJson?.variants) return;

//   const allSelectors = picker.querySelectorAll(".option-selector-upsell");

//   allSelectors.forEach((selector) => {
//     const optionName = selector.dataset.option;
//     const optionsLis = selector.querySelectorAll("li");

//     optionsLis.forEach((li) => {
//       const thisValue = li.getAttribute("value");

//       // Clone the current selection and simulate choosing this option
//       const tempSelection = { ...selectedOptions, [optionName]: thisValue };

//       // Find all variants that match this combination
//       const matchingVariants = productJson.variants.filter((variant) => {
//         return Object.entries(tempSelection).every(([name, val], idx) => {
//           return val === variant[`option${idx + 1}`];
//         });
//       });

//       // Check if at least one matching variant exists AND is available
//       const comboExistsAndAvailable = matchingVariants.some(v => v.available === true);

//       // Toggle unavailable class based on existence + availability
//       if (comboExistsAndAvailable) {
//         li.classList.remove("unavailable");
//       } else {
//         li.classList.add("unavailable");
//       }
//     });
//   });
// }
function updateUnavailableOptions(picker, productJson) {
  if (!picker || !productJson?.variants) return;

  // 🧩 Step 1: Get selected options WITH option index
  const selectedOptions = {};

  picker.querySelectorAll(".option-selector-upsell").forEach(selector => {
    const selectedLi = selector.querySelector("li.selected");
    if (selectedLi) {
      const optIndex = Number(selectedLi.dataset.optionnumbers); // 1,2,3
      selectedOptions[optIndex] = selectedLi.getAttribute("value");
    }
  });

  // 🧩 Step 2: Loop through each selector
  picker.querySelectorAll(".option-selector-upsell").forEach(selector => {
    const lis = selector.querySelectorAll("li");

    lis.forEach(li => {
      const thisValue = li.getAttribute("value");
      const thisOptIndex = Number(li.dataset.optionnumbers);

      // 🧩 Step 3: Simulate selection correctly
      const tempSelection = { ...selectedOptions, [thisOptIndex]: thisValue };

      // 🧩 Step 4: Match variants using REAL option numbers
      const isAvailable = productJson.variants.some(variant => {
        if (!variant.available) return false;

        return Object.entries(tempSelection).every(([optIdx, val]) => {
          return variant[`option${optIdx}`] === val;
        });
      });

      // 🧩 Step 5: Update UI
      li.classList.toggle("unavailable", !isAvailable);
    });
  });
}





 // CUSTOM CODE on submit to show/hide select size message START
document.querySelectorAll('variant-picker fieldset.option-selector[data-selector-type="listed"]').forEach((fieldset) => {
  const wrapper = fieldset.querySelector('.option-selector__btns');
  const label = fieldset.querySelector('.option-selector .label');
  const optionName = fieldset.dataset.option || 'Option'; // e.g. "Size" or "Color"

  // Validation function for this fieldset
  function validateFieldset() {
    const isSelected = wrapper.querySelector('input[type="radio"]:checked') !== null;

    if (isSelected) {
      // ✅ Restore normal label
      label.classList.remove('label--contains-error');
      label.removeAttribute('aria-live');

      const prefix = label.querySelector('.label__prefix');
      if (prefix) prefix.remove();

      label.textContent = optionName;
      wrapper.closest("fieldset").classList.remove("select_the_option");
    } else {
      // ❌ Add error state
      label.classList.add('label--contains-error');
      label.setAttribute('aria-live', 'polite');

      const existingPrefix = label.querySelector('.label__prefix');
      if (existingPrefix) existingPrefix.remove();

      const prefix = document.createElement('span');
      prefix.className = 'label__prefix';
      prefix.textContent = 'Please select a\u00A0';

      label.textContent = '';
      label.append(prefix, optionName);
      // wrapper.closest("fieldset").classList.add("select_the_option");
    }
  }

  // Run once on load (in case none is selected initially)
  validateFieldset();

  // Run whenever user selects a radio option
  wrapper.addEventListener('change', validateFieldset);
});
 // CUSTOM CODE on submit to show/hide select size message END


});
//  CARD PRODUCT SWATXHES AJAX JS START




// custom code to redirect to the product page to open the product personalizer
  document.addEventListener('click', function (event) {
    const btn = event.target.closest('.cstm-embroidery-btn');
    if (!btn) return; // Exit if the click wasn't on the button

    const url = new URL(window.location.href);
    url.searchParams.set('open_personalization', 'true');
    window.location.href = url.toString();
  });




  // custom code to remove the querystring from url when close the personalization popup
  document.addEventListener('click', function (event) {
    const modalBtn = event.target.closest('.js-modal-close');
    if (!modalBtn) return; // exit if click is not on target

    console.log("🧹 Removing open_personalization from URL");

    const url = new URL(window.location.href);
    url.searchParams.delete('open_personalization');

    // Replace the current URL without reloading
    window.history.replaceState({}, document.title, url.toString());a
  });











  // custom code for main product page on change variant
  (() => {
  let cachedData = new WeakMap();

  const getData = (picker) => {
    if (cachedData.has(picker)) return cachedData.get(picker);

    const jsonEl = picker.querySelector('script[type="application/json"]');
    if (!jsonEl) return null;

    const data = JSON.parse(jsonEl.textContent);
    cachedData.set(picker, data);
    return data;
  };

  const updateAvailability = (picker) => {
    const data = getData(picker);
    if (!data) return;

    const fieldsets = [...picker.querySelectorAll('fieldset')];

    const selected = fieldsets.map(fs =>
      fs.querySelector('input[type="radio"]:checked')?.value || null
    );

    fieldsets.forEach((fs, optionIndex) => {
      fs.querySelectorAll('input[type="radio"]').forEach(input => {
        const testSelection = [...selected];
        testSelection[optionIndex] = input.value;

        const isAvailable = data.variants.some(v =>
          v.available &&
          v.options.every((opt, i) =>
            !testSelection[i] || opt === testSelection[i]
          )
        );

        const label =
          fs.querySelector(`label[for="${input.id}"]`) ||
          input.closest('label');

        if (label) {
          label.classList.toggle('opt_not_available', !isAvailable);
        }

        input.disabled = !isAvailable;
      });
    });
  };

  /* ✅ EVENT DELEGATION */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('variant-picker input[type="radio"]');
    if (!input) return;

    const picker = input.closest('variant-picker');
    if (!picker) return;

    updateAvailability(picker);
  });

  /* ✅ INITIAL RUN (for first load only) */
  document.querySelectorAll('variant-picker').forEach(updateAvailability);

  console.log('Variant availability (delegated) ready ✅');
})();

//   (() => {
//   const picker = document.querySelector('variant-picker');
//   if (!picker) return console.error('variant-picker not found');

//   const jsonEl = picker.querySelector('script[type="application/json"]');
//   if (!jsonEl) return console.error('Variant JSON not found');

//   const data = JSON.parse(jsonEl.textContent);

//   const updateAvailability = () => {
//     const fieldsets = [...picker.querySelectorAll('fieldset')];

//     const selected = fieldsets.map(fs =>
//       fs.querySelector('input[type="radio"]:checked')?.value || null
//     );

//     fieldsets.forEach((fs, optionIndex) => {
//       fs.querySelectorAll('input[type="radio"]').forEach(input => {
//         const testSelection = [...selected];
//         testSelection[optionIndex] = input.value;

//         const isAvailable = data.variants.some(v =>
//           v.available &&
//           v.options.every((opt, i) =>
//             !testSelection[i] || opt === testSelection[i]
//           )
//         );

//         // label may not exist in some themes
//         const label =
//           fs.querySelector(`label[for="${input.id}"]`) ||
//           input.closest('label');

//         if (label) {
//           label.classList.toggle('opt_not_available', !isAvailable);
//         }

//         input.disabled = !isAvailable;
//       });
//     });
//   };

//   picker.addEventListener('change', updateAvailability);
//   updateAvailability(); // initial run

//   console.log(`Option availability handler ready (${data.options.length} options) ✅`);
// })();
